import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { NumericKeypad } from './NumericKeypad'

/**
 * One counting step: a big value box, a full numeric keypad, and the expected
 * figure to check against.
 *
 * The keypad is the point (F-CTRL-N33). Cash always had one; card and online
 * had two bare inputs, so on a touchscreen till — no physical keyboard — their
 * totals literally could not be typed, and Skip was the only way forward.
 *
 * There is deliberately NO `placeholder="0"`. The grey placeholder zero on the
 * old card/online inputs was read as an entered value: the field looked like it
 * said 0, so operators moved on believing they had counted it, and the day
 * closed with the tender stored as "never counted".
 */
export function TenderCountStep({
  id,
  label,
  value,
  onChange,
  expectedLabel,
  reconciliation,
  crossDayNote,
}: {
  id: string
  label: string
  value: string
  onChange: (next: string) => void
  expectedLabel?: string
  reconciliation?: { matches: boolean; text: string }
  crossDayNote?: string
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={id} className="text-base">
          {label}
        </Label>
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
          className="h-16 text-4xl font-semibold tabular-nums text-center"
        />
        {expectedLabel && <p className="text-sm text-muted-foreground">{expectedLabel}</p>}
        {reconciliation && (
          <p
            className={cn(
              'text-sm font-medium',
              reconciliation.matches ? 'text-emerald-600' : 'text-amber-600',
            )}
          >
            {reconciliation.text}
          </p>
        )}
        {crossDayNote && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            {crossDayNote}
          </p>
        )}
      </div>
      <NumericKeypad value={value} onChange={onChange} />
    </div>
  )
}
